import { useState, useEffect } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Flame, ChevronLeft, ChevronRight, ExternalLink, Newspaper, Sparkles } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { API, FALLBACK_IMG, slugifyMedia } from "@/lib/newsUtils";
import { trackOpen } from "@/lib/engagement";

export function HighlightsCarousel() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/highlights`);
        setItems(data.items || []);
      } catch { /* silencioso */ }
    })();
  }, []);

  if (items.length === 0) return null;

  const current = items[idx];
  const openItem = (a) => { setActive(a); setOpen(true); trackOpen(a.source); };
  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + items.length) % items.length); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % items.length); };
  const advance = () => setIdx((i) => (i + 1) % items.length);

  return (
    <>
      <div
        data-testid="highlights-carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onClick={() => openItem(current)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") openItem(current); }}
        className="group relative cursor-pointer border border-border hover:border-primary transition-colors bg-card"
      >
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3">
          <div className="flex items-center gap-1.5 text-primary shrink-0">
            <Flame className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-widest font-bold hidden sm:inline">Destacados</span>
          </div>
          <div className="w-px h-5 bg-border hidden sm:block" />
          <button onClick={prev} data-testid="highlights-prev" aria-label="Anterior"
            className="text-muted-foreground hover:text-primary transition-colors shrink-0">
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex-1 min-w-0 h-[3.75rem] sm:h-[4.5rem] flex items-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4 }}
                className="flex items-start gap-3 min-w-0 w-full"
              >
                <Badge className="rounded-none bg-foreground text-background hover:bg-foreground font-medium uppercase tracking-wide text-[10px] shrink-0 hidden md:inline-flex mt-0.5">
                  {current.source}
                </Badge>
                <p data-testid="highlights-title" className="text-sm sm:text-base font-semibold text-foreground leading-snug line-clamp-3">
                  {current.title}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          <button onClick={next} data-testid="highlights-next" aria-label="Siguiente"
            className="text-muted-foreground hover:text-primary transition-colors shrink-0">
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="text-[11px] text-muted-foreground tabular-nums shrink-0 hidden sm:inline">
            {idx + 1}/{items.length}
          </span>
        </div>

        {items.length > 1 && (
          <div className="h-0.5 bg-border/60">
            <div
              key={idx}
              data-testid="highlights-progress"
              className="hl-progress-bar h-full bg-primary"
              style={{ animationPlayState: paused || open ? "paused" : "running" }}
              onAnimationEnd={advance}
            />
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none p-0 overflow-hidden max-w-lg gap-0" data-testid="highlights-modal">
          {active && (
            <div>
              {active.image && (
                <div className="h-52 bg-muted overflow-hidden border-b border-border">
                  <img
                    src={active.image}
                    alt={active.title}
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                  />
                </div>
              )}
              <div className="p-6">
                <Badge className="rounded-none bg-foreground text-background hover:bg-foreground font-medium uppercase tracking-wide text-[11px]">
                  {active.source}
                </Badge>
                <h2 className="font-heading text-xl font-bold leading-snug tracking-tight mt-3 mb-4 pr-6" data-testid="highlights-modal-title">
                  {active.title}
                </h2>
                <div className="pl-4 border-l-2 border-primary mb-6">
                  <div className="flex items-center gap-1.5 mb-2 text-primary">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span className="text-[11px] uppercase tracking-widest font-semibold">Resumen Gemini</span>
                  </div>
                  <p className="text-sm leading-relaxed text-foreground/80">
                    {active.summary || "Resumen no disponible."}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <a
                    href={active.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="highlights-read-original"
                    onClick={() => trackOpen(active.source)}
                    className="inline-flex items-center justify-center gap-2 flex-1 h-11 bg-primary text-primary-foreground text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-blue-900"
                  >
                    Leer noticia original
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <Link
                    to={`/medios/${slugifyMedia(active.source)}`}
                    data-testid="highlights-more-media"
                    onClick={() => trackOpen(active.source)}
                    className="inline-flex items-center justify-center gap-2 flex-1 h-11 border border-primary text-primary text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-primary hover:text-white"
                  >
                    <Newspaper className="w-4 h-4" />
                    Ver más del medio
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
